import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { faCircleMinus, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { ScoreCriteriaType } from "../schema/score.schema";

import { issueText } from "./issueText";
import { swatchOf } from "./ScoreMatrix/matrixModel";
import { CellAssignment } from "./scoreConfigView";
import { ScoreIssue } from "./scoreIssues";

export interface PreviewPair {
	key: string;
	label: string;
	assignment: CellAssignment | null;
}

interface RowPreviewProps {
	title: string;
	description: string;
	pairsTitle: string;
	pairs: PreviewPair[];
	emptyPairs: string;
	criteria: ScoreCriteriaType[];
	issues: ScoreIssue[];
	/** Shown when the row itself is switched off. */
	deactivatedNote?: string;
	testId?: string;
}

/**
 * The hover card behind a segment or persona chip (PREC-4055): description,
 * the rows on the other axis with the lead scoring each pair gets, and the
 * open issues. One component read from either side.
 */
export const RowPreview: FunctionComponent<RowPreviewProps> = ({
	title,
	description,
	pairsTitle,
	pairs,
	emptyPairs,
	criteria,
	issues,
	deactivatedNote,
	testId,
}) => {
	const { t } = useTranslation();
	const sectionTitle = "mb-1 text-[10px] font-bold uppercase tracking-wider text-text-disabled";

	return (
		<div className="w-[400px] text-left text-xs" data-testid={testId}>
			<div className="border-b border-border-light px-1 pb-2">
				<div className="text-sm font-semibold text-text">
					{title || t("leadScoring.shell.untitled")}
				</div>
				<div className="mt-0.5 text-text-meta">
					{description || t("leadScoring.detail.noDescriptionYet")}
				</div>
			</div>
			<div className="px-1 pt-2">
				<div className={sectionTitle}>{pairsTitle}</div>
				{pairs.length === 0 && <div className="text-text-disabled">{emptyPairs}</div>}
				{pairs.map((pair) => (
					<div
						key={pair.key}
						className="grid grid-cols-[minmax(0,1fr)_140px] items-center gap-2 py-0.5"
					>
						<span className="truncate text-text">{pair.label}</span>
						{pair.assignment ? (
							<span className="flex items-center gap-1.5 truncate text-text-meta">
								<span
									className={classNames(
										"h-2 w-2 shrink-0 rounded-full",
										swatchOf(criteria, pair.assignment.criteriaId)
									)}
								/>
								{pair.assignment.criteriaName}
							</span>
						) : (
							<span className="flex items-center gap-1 text-status-error">
								<Icon className="h-2.5 w-2.5" icon={faTriangleExclamation} />
								{t("leadScoring.matrix.missingShort")}
							</span>
						)}
					</div>
				))}
				{deactivatedNote && (
					<div className="mt-1.5 flex items-start gap-1.5 text-tag-warning-dark">
						<Icon className="mt-0.5 h-3 w-3 shrink-0" icon={faCircleMinus} />
						{deactivatedNote}
					</div>
				)}
				{issues.length > 0 && (
					<>
						<div className={classNames(sectionTitle, "mt-2")}>{t("leadScoring.detail.issues")}</div>
						{issues.map((issue, index) => (
							<div key={index} className="flex items-start gap-1.5 py-0.5 text-status-error">
								<Icon className="mt-0.5 h-3 w-3 shrink-0" icon={faTriangleExclamation} />
								{issueText(t, issue)}
							</div>
						))}
					</>
				)}
				<div className="mt-2 text-[11px] text-text-disabled">
					{t("leadScoring.detail.clickToEdit")}
				</div>
			</div>
		</div>
	);
};
